import Avatar from 'ext-ui/components/Avatar/Avatar'
import React, { ReactElement } from 'react'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'

import { PhysicalCardManagementStatus } from '../cardDetailHeroDemoData'
import { initialsFrom, toDisplayAmount } from '../cardDetailHeroFormat'
import CardNickname from '../CardNickname/CardNickname'
import StatusPill from '../StatusPill/StatusPill'
import { useCardDetailHeroData } from '../useCardDetailHeroData'
import {
  useIdentityStateText,
  useLimitsStateText,
} from '../useCardDetailHeroStateText'
import * as style from './CardSummary.style'

// The currency-aware `toDisplayAmount` (review finding #1) lives in
// cardDetailHeroFormat, imported above, so both hero variants share it.

type CardSummaryProps = {
  /**
   * The card whose identity and limits this summary reads. Supplied by the page
   * (features never resolve routing), and threaded down so the region reads the
   * same seam the shell does — React Query dedupes both observers.
   */
  creditCardId: string | undefined
  /**
   * Card status is owned by the hero shell — so the pill, the card-art overlay,
   * and the freeze button never diverge — and passed in, rather than read from
   * the seam here. `undefined` while identity loads, or when the backend status
   * has no card-management counterpart: better no pill than a wrong one.
   */
  status: PhysicalCardManagementStatus | undefined
  /**
   * Whether this user may rename the card (`canUpdateCreditCard`). Resolved by
   * the page alongside the card's other permissions and passed down, rather than
   * fetched here — the same reason `status` is a prop.
   */
  canUpdateCard: boolean
  /**
   * Whether this variant is the on-screen one. The full hero and the pinned
   * compact bar render the SAME group-state messages and are both always
   * mounted, so without this both would be live regions and a single failure
   * could be announced twice. The off-screen variant keeps the visible text and
   * drops only the live-region role.
   */
  announceStates: boolean
  /**
   * EX-40596 source selector, shared with the card-details reveal: false renders
   * demo limits, true reads live ones. Resolved by the page and threaded down.
   */
  cardHeaderLiveDataEnabled: boolean
}

const CardSummary: React.FC<CardSummaryProps> = ({
  creditCardId,
  status,
  canUpdateCard,
  announceStates,
  cardHeaderLiveDataEnabled,
}): ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const identityStateText = useIdentityStateText()
  const limitsStateText = useLimitsStateText()
  const { card, cardState, limits, limitsState, renameCard, renameState } =
    useCardDetailHeroData(creditCardId, cardHeaderLiveDataEnabled)

  return (
    <div css={style.wrapper}>
      {card !== undefined ? (
        <div>
          <div css={style.identityRow}>
            <Avatar
              size={28}
              fallback={
                <span className="fallback-text">
                  {initialsFrom(card.cardholder.displayName)}
                </span>
              }
            />
            <h2 css={style.cardholderName} data-testid="cardholder-name">
              {card.cardholder.displayName}
            </h2>
          </div>
          <div css={style.metaRow}>
            <span css={style.metaText}>
              <CardNickname
                nickname={card.nickname}
                canRename={canUpdateCard}
                onRename={renameCard}
                isSaving={renameState.isPending}
                isError={renameState.isError}
              />
              {' · '}
              {translate('Physical')}
              {` · •• ${card.last4}`}
            </span>
            {status !== undefined && <StatusPill status={status} />}
          </div>
        </div>
      ) : (
        // Identity is its own group, so it announces its own state — a limits
        // failure below can never blank this row, and vice versa.
        <p
          css={style.metaText}
          role={
            announceStates
              ? cardState === 'loading'
                ? 'status'
                : 'alert'
              : undefined
          }
          data-testid="card-summary-identity-state"
        >
          {identityStateText(cardState)}
        </p>
      )}
      <div css={style.creditBlock}>
        {limits !== undefined ? (
          <>
            <span css={style.creditLabel}>{translate('Available credit')}</span>
            <span css={style.creditAmount}>
              {currencyFormatter({
                // Not clamped at zero: an over-limit card really does have a
                // negative available spend, and the contract preserves it.
                amount: toDisplayAmount(limits.cardFinancials.availableSpend),
                currency: limits.cardFinancials.availableSpend
                  .currency as ExtendCurrency,
                maximumFractionDigits: 0,
              })}
            </span>
            <span css={style.creditLimit}>
              {translate('of ${limit} limit', {
                limit: currencyFormatter({
                  amount: toDisplayAmount(
                    limits.permanentCardSpendControl.limit,
                  ),
                  currency: limits.permanentCardSpendControl.limit
                    .currency as ExtendCurrency,
                  maximumFractionDigits: 0,
                }),
              })}
            </span>
            {limits.provenance === 'SURROGATE_PARTNER' && (
              <span
                css={style.creditProvenance}
                data-testid="card-summary-provenance"
              >
                {translate('Simulated balance, not a live issuer figure')}
              </span>
            )}
          </>
        ) : (
          <span
            css={style.creditLimit}
            role={
              announceStates
                ? limitsState === 'loading'
                  ? 'status'
                  : 'alert'
                : undefined
            }
            data-testid="card-summary-limits-state"
          >
            {limitsStateText(limitsState)}
          </span>
        )}
      </div>
    </div>
  )
}

export default CardSummary
